export type StageId =
  | "venue"
  | "profile"
  | "direction"
  | "experiment"
  | "paper"
  | "prompt"
  | "workspace";

export type PromptMode =
  | "master"
  | "landscape"
  | "experiment"
  | "paper"
  | "figure"
  | "review"
  | "submission";

export type AiTask =
  | "requirement_intake"
  | "paper_intake"
  | "venue_analysis"
  | "research_interview"
  | "direction_candidates"
  | "experiment_review"
  | "paper_plan"
  | "context_compression"
  | "prompt_refinement"
  | "workspace_analysis";

export type AiMode = "proxy" | "direct";
export type AiProvider =
  | "deepseek"
  | "openai"
  | "openrouter"
  | "siliconflow"
  | "ollama"
  | "custom";

export interface AiConfig {
  mode: AiMode;
  provider: AiProvider;
  baseUrl: string;
  model: string;
  apiKey: string;
  persistKey: boolean;
  jsonMode: boolean;
}

export interface VenueSource {
  id: string;
  url: string;
  title: string;
  excerpt: string;
  text: string;
  fetchedAt: string;
  status: "ready" | "error";
  error?: string;
}

export interface VenueFact {
  label: string;
  value: string;
  evidenceUrl: string;
  confidence: "high" | "medium" | "low";
}

export interface InterviewQuestion {
  id: string;
  question: string;
  why: string;
  answerType: "short" | "long" | "choice";
  options?: string[];
}

export interface VenueBrief {
  venueName: string;
  track: string;
  summary: string;
  facts: VenueFact[];
  unknowns: string[];
  warnings: string[];
  questions: InterviewQuestion[];
}

export interface ResearchProfile {
  domain: string;
  experience: string;
  strengths: string;
  priorAssets: string;
  avoidTopics: string;
  compute: string;
  modelAccess: string;
  dataAccess: string;
  budget: string;
  deadline: string;
  riskTolerance: "conservative" | "balanced" | "ambitious";
  preferredContributions: string[];
}

export interface DirectionCandidate {
  id: string;
  title: string;
  oneLine: string;
  researchQuestion: string;
  unresolvedGap: string;
  proposedMechanism: string;
  benchmarkPlan: string;
  primaryMetric: string;
  baselines: string[];
  novelty: number;
  feasibility: number;
  evidenceReadiness: number;
  risks: string[];
  pivotSignal: string;
}

export interface ExperimentPlan {
  hypothesis: string;
  taskDefinition: string;
  implementationStack: string;
  runMatrix: string;
  datasets: string;
  evaluationProtocol: string;
  primaryMetric: string;
  secondaryMetrics: string;
  baselines: string;
  mainComparison: string;
  ablations: string;
  hyperparameters: string;
  robustness: string;
  caseStudy: string;
  seeds: string;
  computeBudget: string;
  artifactPlan: string;
  reportingPlan: string;
  stopCriteria: string;
  pivotCriteria: string;
  requiredEvidence: string[];
  review?: {
    score: number;
    summary: string;
    gaps: string[];
    actions: string[];
    redFlags: string[];
  };
}

export interface RequirementPatch {
  projectName?: string;
  idea?: string;
  venueUrls?: string;
  venueManualText?: string;
  profile?: Partial<ResearchProfile>;
  experiment?: Partial<ExperimentPlan>;
  paper?: Partial<PaperPlan>;
}

export interface RequirementIntakeResult {
  summary: string;
  detectedStages: StageId[];
  confidence: "high" | "medium" | "low";
  warnings: string[];
  patch: RequirementPatch;
  mappings: Array<{
    sourceQuote: string;
    targetField: string;
    value: string;
    confidence: "high" | "medium" | "low";
  }>;
}

export interface ReferencePaper {
  id: string;
  name: string;
  size: number;
  pageCount: number;
  charCount: number;
  extractedAt: string;
}

export interface FigureSpec {
  id: string;
  role: string;
  message: string;
  evidence: string;
  format: string;
}

export interface PaperPlan {
  workingTitle: string;
  centralClaim: string;
  contributions: string;
  narrative: string;
  sections: string;
  citationRules: string;
  figureSkill: string;
  figures: FigureSpec[];
  reviewRounds: string;
  writingConstraints: string;
  generatedPlan?: {
    story: string;
    contributions: string[];
    sectionLogic: string[];
    figureBriefs: string[];
    citationPlan: string[];
    likelyReviewerQuestions: string[];
  };
}

export interface ContextCapsule {
  id: string;
  stage: StageId;
  title: string;
  summary: string;
  decisions: string[];
  evidence: string[];
  constraints: string[];
  openQuestions: string[];
  artifactRefs: string[];
  sourceRefs: string[];
  discardedDetail: string[];
  tokenEstimate: number;
  createdAt: string;
}

export interface WorkspaceFile {
  path: string;
  name: string;
  extension: string;
  size: number;
  modifiedAt: number;
}

export interface WorkspaceCheck {
  id: string;
  label: string;
  status: "complete" | "partial" | "missing";
  detail: string;
  matchedFiles: string[];
}

export interface WorkspaceReport {
  scannedAt: string;
  rootName: string;
  fileCount: number;
  files: WorkspaceFile[];
  checks: WorkspaceCheck[];
  score: number;
  snippets: Array<{ path: string; text: string }>;
  aiAnalysis?: {
    summary: string;
    score: number;
    strengths: string[];
    gaps: string[];
    nextActions: string[];
    habitWarnings: string[];
  };
}

export interface ProjectMemory {
  contextBudget: number;
  capsules: ContextCapsule[];
}

export interface WorkspaceState {
  absolutePath: string;
  handleName: string;
  report?: WorkspaceReport;
  scaffoldedAt?: string;
}

export interface ProjectState {
  version: 1;
  id: string;
  projectName: string;
  idea: string;
  venueUrls: string;
  venueManualText: string;
  venueSources: VenueSource[];
  venueBrief?: VenueBrief;
  profile: ResearchProfile;
  interviewAnswers: Record<string, string>;
  directions: DirectionCandidate[];
  selectedDirectionId: string;
  experiment: ExperimentPlan;
  paper: PaperPlan;
  promptMode: PromptMode;
  refinedPrompts: Partial<Record<PromptMode, string>>;
  selectedSkills: string[];
  stageNotes: Partial<Record<StageId, string>>;
  referencePapers: ReferencePaper[];
  memory: ProjectMemory;
  workspace: WorkspaceState;
  createdAt: string;
  updatedAt: string;
}

export interface AiLog {
  id: string;
  projectId: string;
  task: AiTask;
  model: string;
  mode: AiMode;
  startedAt: string;
  completedAt: string;
  request: unknown;
  response: unknown;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
  error?: string;
}

export interface AiResult<T = unknown> {
  data: T;
  model: string;
  usage?: AiLog["usage"];
}

export const STAGES: Array<{
  id: StageId;
  number: string;
  label: string;
  helper: string;
}> = [
  { id: "venue", number: "01", label: "起点", helper: "venue 与设想" },
  { id: "profile", number: "02", label: "画像", helper: "长处与约束" },
  { id: "direction", number: "03", label: "选题", helper: "问题与机制" },
  { id: "experiment", number: "04", label: "证据", helper: "实验契约" },
  { id: "paper", number: "05", label: "论文", helper: "叙事与图表" },
  { id: "prompt", number: "06", label: "指令", helper: "交付给 Codex" },
  { id: "workspace", number: "07", label: "工作区", helper: "检查与纠错" },
];

export const PROMPT_MODES: Array<{
  id: PromptMode;
  label: string;
  description: string;
  context: string;
  deliverable: string;
  excludes: string;
}> = [
  {
    id: "master",
    label: "全流程总提示词",
    description: "适合把完整项目一次性交给长程智能体",
    context: "全部已确认信息、阶段附录与技能路由",
    deliverable: "从调研到投稿的阶段化项目",
    excludes: "未确认事实、伪造证据与自动提交",
  },
  {
    id: "landscape",
    label: "调研与选题",
    description: "适合尚未冻结问题与核心机制时使用",
    context: "venue、研究者画像、种子论文与方向偏好",
    deliverable: "文献矩阵、候选方向与冻结决策",
    excludes: "大规模实验、论文正文与 SOTA 承诺",
  },
  {
    id: "experiment",
    label: "实验执行",
    description: "适合问题、机制与公平协议已经冻结时使用",
    context: "方向、资源、实验契约与参考论文",
    deliverable: "代码、原始记录、分析与停止决策",
    excludes: "venue 背景、章节写作与投稿格式",
  },
  {
    id: "paper",
    label: "论文写作",
    description: "适合实验产物与引用已经冻结时使用",
    context: "核心主张、冻结结果、论文结构与图表计划",
    deliverable: "论证合同、TeX、引用与视觉 QA",
    excludes: "重新设计实验、反推结果与虚构引用",
  },
  {
    id: "figure",
    label: "SCI 绘图",
    description: "适合单独委托框架图或实验图制作",
    context: "图表角色、信息、证据边界与格式",
    deliverable: "brief、可编辑源、导出图与 QA",
    excludes: "修改数字、改变主张与迁就版式",
  },
  {
    id: "review",
    label: "评审修改",
    description: "适合已有完整稿件或审稿意见时使用",
    context: "核心主张、论文主线、评审策略与意见",
    deliverable: "问题分级、修改追踪与修订稿",
    excludes: "逐条盲从、破坏主线与自造负面结论",
  },
  {
    id: "submission",
    label: "投稿交付",
    description: "适合科学内容冻结后的最终交付",
    context: "官方规则、论文元数据与本地工作区",
    deliverable: "PDF、源码包、表单草稿、清单与哈希",
    excludes: "改写科学内容、修改数字与自动提交",
  },
];
